import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BUSINESS_TYPES = [
  { id: 'ecommerce', label: 'E-Commerce', description: 'Shopify, Amazon, Etsy, TikTok Shop, WooCommerce' },
  { id: 'crypto', label: 'Crypto & Digital Assets', description: 'Traders, DeFi, NFTs, staking, Web3' },
  { id: 'creator', label: 'Content Creator', description: 'YouTube, TikTok, OnlyFans, Patreon, podcasts' }
];

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    businessTypes: []
  });

  const toggleBusinessType = (typeId) => {
    setFormData(prev => ({
      ...prev,
      businessTypes: prev.businessTypes.includes(typeId)
        ? prev.businessTypes.filter(t => t !== typeId)
        : [...prev.businessTypes, typeId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      const user = await register(
        formData.email, 
        formData.password, 
        formData.name,
        formData.businessTypes
      );
      toast.success('Account created successfully');
      navigate(user.role === 'accountant' ? '/accountant' : '/portal');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 font-inter text-sm text-iron-grey hover:text-obsidian transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back to home
        </Link>
        
        {/* Register Card */}
        <div className="bg-white border border-iron-grey p-8 card-shadow">
          <div className="mb-8">
            <h1 className="font-fraunces font-bold text-3xl text-obsidian mb-2">Create Account</h1>
            <p className="font-inter text-sm text-iron-grey">Join Oakmere Advisory's client portal</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-inter text-sm text-obsidian mb-1 block">Full Name</label>
              <Input 
                type="text"
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-parchment border-iron-grey text-obsidian placeholder:text-iron-grey"
                data-testid="register-name"
              />
            </div>
            
            <div>
              <label className="font-inter text-sm text-obsidian mb-1 block">Email</label>
              <Input 
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-parchment border-iron-grey text-obsidian placeholder:text-iron-grey"
                data-testid="register-email"
              />
            </div>
            
            <div>
              <label className="font-inter text-sm text-obsidian mb-1 block">Password</label>
              <div className="relative">
                <Input 
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min. 8 characters"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="bg-parchment border-iron-grey text-obsidian placeholder:text-iron-grey pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-iron-grey hover:text-obsidian"
                >
                  {showPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="font-inter text-sm text-obsidian mb-1 block">Confirm Password</label>
              <Input 
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                className="bg-parchment border-iron-grey text-obsidian placeholder:text-iron-grey"
                data-testid="register-confirm-password"
              />
            </div>

            {/* Business Type Selection */}
            <div>
              <label className="font-inter text-sm text-obsidian mb-2 block">
                What type of business do you run? <span className="text-iron-grey">(Select all that apply)</span>
              </label>
              <div className="space-y-2">
                {BUSINESS_TYPES.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => toggleBusinessType(type.id)}
                    className={`w-full p-3 border text-left transition-all ${
                      formData.businessTypes.includes(type.id)
                        ? 'border-obsidian bg-obsidian text-parchment'
                        : 'border-iron-grey bg-parchment text-obsidian hover:border-obsidian'
                    }`}
                    data-testid={`business-type-${type.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-inter text-sm font-medium">{type.label}</p>
                        <p className={`font-inter text-xs ${
                          formData.businessTypes.includes(type.id) ? 'text-parchment/70' : 'text-iron-grey'
                        }`}>
                          {type.description}
                        </p>
                      </div>
                      {formData.businessTypes.includes(type.id) && (
                        <Check size={18} strokeWidth={2} />
                      )}
                    </div>
                  </button>
                ))}
              </div>
              <p className="font-inter text-xs text-iron-grey mt-2">
                This helps us show you the relevant document checklist. You can change this later.
              </p>
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full btn-primary mt-6"
              data-testid="register-submit"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
          
          <div className="mt-6 pt-6 border-t border-iron-grey text-center">
            <p className="font-inter text-sm text-iron-grey">
              Already have an account?{' '}
              <Link to="/login" className="text-obsidian hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
        
        {/* Brand */}
        <div className="text-center mt-8">
          <span className="text-[16px] font-bold text-obsidian tracking-widest" style={{ fontFamily: "'Glacial Indifference', sans-serif" }}>
            OAKMERE ADVISORY
          </span>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
